import { createAsyncThunk } from '@reduxjs/toolkit';
import { RootState } from 'modules/store';
import { ErrorObject } from 'types/global';
import Errors from 'modules/shared/error/errors';
import SupportAlertEmailsService from './supportAlertEmails.service';
import {
  NotificationEmailsResponse,
  AddEmailParams,
  UpdateEmailParams,
  DeleteEmailParams,
} from './supportAlertEmails.types';

export const getEmails = createAsyncThunk<
  NotificationEmailsResponse,
  void,
  {
    state: RootState;
    rejectValue: ErrorObject;
  }
>('supportAlertEmails/get-emails', async (_, { getState, rejectWithValue }) => {
  const { environment: appEnvironment } = getState().root.global;

  try {
    return await SupportAlertEmailsService.getEmails({
      appEnvironment,
    });
  } catch (error: unknown) {
    const errorObj: ErrorObject = error as ErrorObject;
    Errors.handle(errorObj);
    return rejectWithValue(errorObj);
  }
});

export const addEmail = createAsyncThunk<
  any,
  Omit<AddEmailParams, 'appEnvironment'>,
  {
    state: RootState;
    rejectValue: ErrorObject;
  }
>(
  'supportAlertEmails/add-email',
  async ({ email, subscriptions }, { getState, rejectWithValue }) => {
    const { environment: appEnvironment } = getState().root.global;

    try {
      return await SupportAlertEmailsService.addEmail({
        appEnvironment,
        email,
        subscriptions,
      });
    } catch (error: unknown) {
      const errorObj: ErrorObject = error as ErrorObject;
      Errors.handle(errorObj);
      return rejectWithValue(errorObj);
    }
  },
);

export const updateEmail = createAsyncThunk<
  any,
  Omit<UpdateEmailParams, 'appEnvironment'>,
  {
    state: RootState;
    rejectValue: ErrorObject;
  }
>(
  'supportAlertEmails/update-email',
  async ({ emailId, subscriptions }, { getState, rejectWithValue }) => {
    const { environment: appEnvironment } = getState().root.global;

    try {
      return await SupportAlertEmailsService.updateEmail({
        appEnvironment,
        emailId,
        subscriptions,
      });
    } catch (error: unknown) {
      const errorObj: ErrorObject = error as ErrorObject;
      Errors.handle(errorObj);
      return rejectWithValue(errorObj);
    }
  },
);

export const deleteEmail = createAsyncThunk<
  any,
  Omit<DeleteEmailParams, 'appEnvironment'>,
  {
    state: RootState;
    rejectValue: ErrorObject;
  }
>(
  'supportAlertEmails/delete-email',
  async ({ emailId }, { getState, rejectWithValue }) => {
    const { environment: appEnvironment } = getState().root.global;

    try {
      return await SupportAlertEmailsService.deleteEmail({
        appEnvironment,
        emailId,
      });
    } catch (error: unknown) {
      const errorObj: ErrorObject = error as ErrorObject;
      Errors.handle(errorObj);
      return rejectWithValue(errorObj);
    }
  },
);

export const saveChanges = createAsyncThunk<
  NotificationEmailsResponse,
  {
    email: string;
    alerts: string[];
    deletedIds: string[];
    updatedSubscriptions: Array<{ emailId: string; subscriptions: string[] }>;
  },
  {
    state: RootState;
    rejectValue: ErrorObject;
  }
>(
  'supportAlertEmails/save-changes',
  async (
    { email, alerts, deletedIds, updatedSubscriptions },
    { getState, rejectWithValue, dispatch },
  ) => {
    try {
      const operations: Promise<any>[] = [];

      // Add new email if provided
      if (email.trim() && alerts.length > 0) {
        operations.push(
          dispatch(addEmail({ email: email.trim(), subscriptions: alerts })),
        );
      }

      // Delete emails
      deletedIds.forEach(emailId => {
        operations.push(dispatch(deleteEmail({ emailId })));
      });

      // Update existing emails
      updatedSubscriptions.forEach(({ emailId, subscriptions }) => {
        operations.push(dispatch(updateEmail({ emailId, subscriptions })));
      });

      await Promise.all(operations);

      // Refetch the updated list
      const result = await dispatch(getEmails());
      return result.payload as NotificationEmailsResponse;
    } catch (error: unknown) {
      const errorObj: ErrorObject = error as ErrorObject;
      Errors.handle(errorObj);
      return rejectWithValue(errorObj);
    }
  },
);
